<template>
    <input v-if="!readonly"
        :type="type"
        style="text-align:right;"
        v-bind:style="styles"
        :class="classes"
        :id="elementId"
        :disabled="disabled"
        autocomplete="off"
        spellcheck="false"
        :autofocus="autofocus"
        :name="name"
        :value="formatterValue"
        :placeholder="placeholder"
        :maxlength="length"
        @keypress="checkInput"
        @paste="checkInput"
        @drop="checkInput"
        @focus="focus"
        @blur="blur"
        @input="change"
        @change="change"
        @mouseup="preventDefault">
        <!-- TODO: @change will call two time -->

    <span v-else style="text-align:right; width: 100%;"
        v-bind:style="styles"
        :class="classes">
        {{prefix}}{{formatterValue}}{{suffix}}
    </span>
</template>
<script>
/* eslint-disable */
import { oneOf, findComponentUpward } from '../assets/js/assist';
import Emitter from '../assets/js/emitter';
import common from 'assets/js/common';
import EventBus from 'assets/js/eventBus';
function addNum (num1, num2) {
    let sq1, sq2, m;
    try {
        sq1 = num1.toString().split('.')[1].length;
    }
    catch (e) {
        sq1 = 0;
    }
    try {
        sq2 = num2.toString().split('.')[1].length;
    }
    catch (e) {
        sq2 = 0;
    }
    m = Math.pow(10, Math.max(sq1, sq2));
    return (Math.round(num1 * m) + Math.round(num2 * m)) / m;
}

export default {
    name: 'InputNumber',
    mixins: [ Emitter ],
    props: {
        max: {
            type: Number,
            default: Infinity
        },
        min: {
            type: Number,
            default: -Infinity
        },
        step: {
            type: Number,
            default: 1
        },
        activeChange: {
            type: Boolean,
            default: true
        },
        value: {
            type: [Number, String]
        },
        type: {
            type: String,
            default: 'num'
        },
        styles: {
            type: String
        },
        classes: {
            type: String
        },
        prefix: {
            type: String
        },
        suffix: {
            type: String
        },
        maxlength: {
            type: Number
        },
        size: {
            validator (value) {
                return oneOf(value, ['small', 'large', 'default']);
            },
            default () {
                return !this.$IVIEW || this.$IVIEW.size === '' ? 'default' : this.$IVIEW.size;
            }
        },
        disabled: {
            type: Boolean,
            default: false
        },
        autofocus: {
            type: Boolean,
            default: false
        },
        readonly: {
            type: Boolean,
            default: false
        },
        editable: {
            type: Boolean,
            default: true
        },
        name: {
            type: String
        },
        precision: {
            type: Number
        },
        elementId: {
            type: String
        },
        formatter: {
            type: Function,
            default: function(value) {
                if (value == "" || value == null || value == undefined) return value;
                let val = Math.round(Number(value) *100) / 100;  
                let parts = value.toString().split('.');
                let num = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',') + (parts[1] ? '.' + parts[1] : '');
                return num;
            }
        },
        parser: {
            type: Function,
            default: value => value.replace(/$s?|(,*)/g, '')
        },
        placeholder: {
            type: String,
            default: ''
        },
    },
    data () {
        return {
            focused: false,
            upDisabled: false,
            downDisabled: false,
            currentValue: this.value,
            regCh: /^[0-9]*$/,
            regAll: /[\W\w]/,
            length: 0
        };
    },
    computed: {
        precisionValue () {
            if(!this.currentValue) return this.currentValue;
            return this.precision ? this.currentValue.toFixed(this.precision) : this.currentValue;
        },
        formatterValue () {
            if (this.formatter && this.precisionValue !== null) {
                return this.formatter(this.precisionValue);
            } else {
                return this.precisionValue;
            }
        }
    },
    methods: {
        preventDefault (e) {
            e.preventDefault();
        },
        up (e) {
            const targetVal = Number(e.target.value);
            if (this.upDisabled && isNaN(targetVal)) {
                return false;
            }
            this.changeStep('up', e);
        },
        down (e) {
            const targetVal = Number(e.target.value);
            if (this.downDisabled && isNaN(targetVal)) {
                return false;
            }
            this.changeStep('down', e);
        },
        changeStep (type, e) {
            if (this.disabled || this.readonly) {
                return false;
            }

            const targetVal = Number(e.target.value);
            let val = Number(this.currentValue);
            const step = Number(this.step);
            if (isNaN(val)) {
                return false;
            }

            // input a number, and key up or down
            if (!isNaN(targetVal)) {
                if (type === 'up') {
                    if (addNum(targetVal, step) <= this.max) {
                        val = targetVal;
                    } else {
                        return false;
                    }
                } else if (type === 'down') {
                    if (addNum(targetVal, -step) >= this.min) {
                        val = targetVal;
                    } else {
                        return false;
                    }
                }
            }

            if (type === 'up') {
                val = addNum(val, step);
            } else if (type === 'down') {
                val = addNum(val, -step);
            }
            this.setValue(val);
        },
        setValue (val) {
            // If step is a decimal and there is no precision set, it is problematic
            if (val && !isNaN(this.precision)) val = Number(Number(val).toFixed(this.precision));

            const {min, max} = this;
            if (val!==null) {
                if (val > max) {
                    val = max;
                } else if (val < min) {
                    val = min;
                }
            }

            this.$nextTick(() => {
                this.currentValue = val;
                this.$emit('input', val);
                this.$emit('on-change', val);
                this.dispatch('FormItem', 'on-form-change', val);
            });
        },
        focus (event) {
            this.focused = true;
            event.target.type = "tel";
            event.target.select();
            this.$emit('on-focus', event);
        },
        blur () {
            this.focused = false;
            event.target.type = "text";
            this.$emit('on-blur', event);
            if (!findComponentUpward(this, ['DatePicker', 'TimePicker', 'Cascader', 'Search'])) {
                this.dispatch('FormItem', 'on-form-blur', this.currentValue);
            }
        },
        change (event) {
            if (event.type == 'input' && !this.activeChange) return;
            event.target.value = event.target.value.trim();
            let val = event.target.value.trim()
            
            if (this.parser) {
                val = this.parser(val);
            }

            const isEmptyString = val.length === 0;
            if(isEmptyString){
                this.setValue(null);
                return;
            }
            if (event.type == 'input' && val.match(/^\-?\.?$|\.$/)) return; // prevent fire early if decimal. If no more input the change event will fire later

            val = Number(val);

            if (!isNaN(val)) {
                this.currentValue = val;
                this.setValue(val);
            } else {
                event.target.value = this.formatter(this.currentValue);
            }

            this.$emit('on-input-change', event);
        },
        changeVal (val) {
            val = Number(val);
            if (!isNaN(val)) {
                const step = this.step;

                this.upDisabled = val + step > this.max;
                this.downDisabled = val - step < this.min;
            } else {
                this.upDisabled = true;
                this.downDisabled = true;
            }
        },
        /**
         * check input event.
         */
        checkInput() {
            try {
                if(this.$el.classList.contains('float')){
                    this.regCh = /^[0-9.]*$/; this.regAll = /^[0-9.]*$/;
                } else if(this.$el.classList.contains('minus')){
                    this.regCh = /^[0-9\-\/]*$/;
                    this.regAll = /^[0-9\-\/]*$/; 
                } else {
                    this.regCh = /^[0-9]*$/; this.regAll = /^[0-9]*$/; 
                }
            } catch(e){}
            switch(event.type){
                case "keypress": event.returnValue = this.regCh.test(String.fromCharCode(event.keyCode));
                    break;
                case "paste":
                    var evt;
                    if(common.isChrome()){
                        evt = event;
                    }else{
                        var evt = window;
                    }
                    event.returnValue = this.regAll.test(String.fromCharCode(evt.clipboardData.getData("text")));
                    if (!event.returnValue) {
                       if (this.regAll.test(evt.clipboardData.getData('text').trim())){
                            event.returnValue = true;
                        }
                    }
                    break;
                case "drop": event.returnValue = this.regAll.test(event.dataTransfer.getData("text")); 
                    break;
                case "blur": if(this.regAll.test(event.srcElement.vlaue)) EventBus.$emit('error-input', this.$el);
                    break;
                default: break;
            }
            if(!event.returnValue && event.returnValue != undefined && event.preventDefault){
                event.preventDefault();
            }
        }
    },
    mounted () {
        let num = Math.pow(10, this.maxlength-1);
        let commas = num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',').split(',')
        if (commas.length > 0) {
            this.length = this.maxlength + commas.length - 1;
        } else {
            this.length = this.maxlength;
        }
        
        if(this.currentValue == undefined) {
            this.currentValue == null;
            this.$emit('input', null);
            return;
        }
        this.changeVal(this.currentValue);
    },
    watch: {
        value (val) {
            this.currentValue = val;
        },
        currentValue (val) {
            this.changeVal(val);
        },
        min () {
            this.changeVal(this.currentValue);
        },
        max () {
            this.changeVal(this.currentValue);
        }
    }
};
</script>
<style scoped>
input {
    padding-top: 1px !important;
}
</style>
